import type {
  AcceptOnBehalfInput,
  AcceptProposalInput,
  ClubDecisionDto,
  ProposalDto,
  RejectOnBehalfInput,
  SaveProposalInput,
} from "@tc/shared";
import { ApiError } from "@/features/trips/api";

const API_URL = import.meta.env.VITE_API_URL ?? "/api";

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    headers: { accept: "application/json", "content-type": "application/json" },
    ...init,
  });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { message?: string | string[] };
      if (body?.message)
        message = Array.isArray(body.message) ? body.message.join(", ") : body.message;
    } catch {
      /* non-JSON error body — keep the status message */
    }
    throw new ApiError(message, res.status);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : null) as T;
}

/** Proposal calls. The proposal is read by both audiences (RLS-scoped); TC builds + sends, the club accepts. */
export const proposalApi = {
  /** The trip's current proposal (latest version), or null if TC hasn't started one (shared read). */
  get: (tripId: string) => req<ProposalDto | null>(`/trips/${tripId}/proposal`),
  /** Save the Draft (back office only). When the latest version is live (Sent/Accepted/Expired)
   *  this creates the revision — a fresh Draft vN+1 from the payload (UAT round 3). */
  save: (tripId: string, body: SaveProposalInput) =>
    req<ProposalDto>(`/trips/${tripId}/proposal`, { method: "PUT", body: JSON.stringify(body) }),
  /** Send the Draft to the club: Draft→Sent + trip Enquiry→ProposalSent (back office only, TW-3445). */
  send: (tripId: string) => req<ProposalDto>(`/trips/${tripId}/proposal/send`, { method: "POST" }),
  /**
   * Accept the live proposal with the club's chosen line items + deposit authorisation: unselected
   * lines are pruned, Sent→Accepted + trip→Accepted (club only, TW-3445 / TW UAT round 1).
   */
  accept: (tripId: string, body: AcceptProposalInput) =>
    req<ProposalDto>(`/trips/${tripId}/proposal/accept`, { method: "POST", body: JSON.stringify(body) }),
  /**
   * Publish the Draft revision directly onto an accepted trip (back office only, UAT round 3
   * follow-up): Draft→Accepted in place, prior version expires, trip stays Accepted, club notified.
   * The re-acceptance route stays on `send`. (Expired-trip resubmission now rides the trip edit —
   * PATCH /trips/:id moves Expired→Enquiry with the field changes.)
   */
  applyUpdate: (tripId: string) =>
    req<ProposalDto>(`/trips/${tripId}/proposal/apply-update`, { method: "POST" }),
  /** What the "Record club decision" dialog needs: live proposal, accept block, restorable version (BO, TRAV-74). */
  clubDecision: (tripId: string) => req<ClubDecisionDto>(`/trips/${tripId}/proposal/club-decision`),
  /** Accept the live (Sent or lapsed) proposal on the club's behalf, with the reason (BO, TRAV-74). */
  acceptOnBehalf: (tripId: string, body: AcceptOnBehalfInput) =>
    req<ProposalDto>(`/trips/${tripId}/proposal/accept-on-behalf`, { method: "POST", body: JSON.stringify(body) }),
  /** Reject the unanswered amendment on the club's behalf, restoring the prior accepted version (BO, TRAV-74). */
  rejectOnBehalf: (tripId: string, body: RejectOnBehalfInput) =>
    req<ProposalDto>(`/trips/${tripId}/proposal/reject-on-behalf`, { method: "POST", body: JSON.stringify(body) }),
};
